import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '@/components/AppShell';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/utils';
import {
  ChevronLeft, Sparkles, TrendingUp, TrendingDown, Minus,
  AlertTriangle, Trophy, Lightbulb, Clock, BarChart3,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import InsightsSummaryCard from './InsightsSummaryCard';

function formatDate(dateString) {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function InsightsDetailView() {
  const { projectId, briefingKey } = useParams();
  const navigate = useNavigate();
  const [briefing, setBriefing] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/daily-briefings/${briefingKey}`);
        setBriefing(res.data.briefing);
        setInsights(res.data.insights);
      } catch (error) {
        console.error('Error fetching briefing detail:', error);
        if (error.response?.status === 404) {
          toast.error('Briefing not found');
          navigate(-1);
        } else {
          toast.error('Failed to load briefing');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [projectId, briefingKey, navigate]);

  if (loading) {
    return (
      <AppShell>
        <div className="px-10 py-6 max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full rounded-[24px]" />
          <Skeleton className="h-48 w-full rounded-[24px]" />
        </div>
      </AppShell>
    );
  }

  if (!briefing) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-20">
          <p className="text-muted-foreground">Briefing not found</p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="overflow-y-auto pb-32">
        {/* Navigation */}
        <div className="sticky top-0 z-10 px-10 py-3 backdrop-blur-xl bg-white/40 dark:bg-neutral-900/40">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
        </div>

        <div className="px-10 py-6 max-w-4xl mx-auto space-y-6">
          {/* Briefing Header */}
          <Card className="rounded-[24px] p-8 glass-elevated animate-fade-up">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-violet-500" />
              </div>
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold">Daily Briefing</h1>
                  <Badge variant="glass">{briefing.project_name || 'Project'}</Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(briefing.generated_at)}
                  </span>
                  {briefing.narrative_status && (
                    <Badge variant="outline" className="text-xs">
                      {briefing.narrative_status}
                    </Badge>
                  )}
                </div>
                {briefing.narrative && (
                  <p className="text-sm text-foreground/90 leading-relaxed whitespace-pre-line">
                    {briefing.narrative}
                  </p>
                )}

                {/* Metrics bar */}
                {briefing.metrics && (
                  <div className="flex items-center gap-4 pt-2">
                    {briefing.metrics.active_alerts != null && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {briefing.metrics.active_alerts} active alert{briefing.metrics.active_alerts !== 1 ? 's' : ''}
                      </span>
                    )}
                    {briefing.metrics.active_goals != null && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <BarChart3 className="w-3.5 h-3.5" />
                        {briefing.metrics.active_goals} active goal{briefing.metrics.active_goals !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Goal Snapshots */}
          {briefing.goal_snapshots && briefing.goal_snapshots.length > 0 && (
            <div className="space-y-2 animate-fade-up" style={{ animationDelay: '60ms' }}>
              <h3 className="text-sm font-semibold text-muted-foreground px-1">
                Goal Snapshots ({briefing.goal_snapshots.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {briefing.goal_snapshots.map((goal) => (
                  <Card key={goal.goal_id} className="rounded-[20px] p-4 glass">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-semibold truncate flex-1">{goal.title}</h5>
                      <Badge variant="outline" className="text-xs ml-2">P{goal.priority}</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-blue-500 transition-all"
                          style={{ width: `${goal.progress_percent || 0}%` }}
                        />
                      </div>
                      <span className="text-xs font-medium tabular-nums">{goal.progress_percent || 0}%</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Insights Section */}
          {insights && (
            <div className="animate-fade-up" style={{ animationDelay: '120ms' }}>
              <h3 className="text-sm font-semibold text-muted-foreground px-1 mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-violet-500" />
                Progress Insights
                {insights.is_baseline && (
                  <Badge variant="outline" className="text-xs">Baseline</Badge>
                )}
              </h3>
              <InsightsSummaryCard insights={insights} />
            </div>
          )}

          {/* Sources Summary */}
          {briefing.sources_summary && briefing.sources_summary.length > 0 && (
            <div className="space-y-2 animate-fade-up" style={{ animationDelay: '180ms' }}>
              <h3 className="text-sm font-semibold text-muted-foreground px-1">Sources</h3>
              <div className="flex flex-wrap gap-2">
                {briefing.sources_summary.map((src, i) => (
                  <Badge key={i} variant="glass" className="text-xs">
                    {src.source_type}: {src.total_changes} change{src.total_changes !== 1 ? 's' : ''}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
